<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue';
import { CoreTypes } from '@vp/shared-types';
import { live, ui } from '../editor/bridge';
import { allLocked, hasGroup, selectedObjects } from '../editor/selection';
import { copyFromMenu, pasteFromMenu } from '../editor/clipboard';
import { copySelectionAsPng } from '../export';
import { t } from '../i18n';
import { useApp } from '../stores/app';
import { contextMenuFor, runCommand } from '../modules';
import RawDataDialog from './RawDataDialog.vue';
import { copyStyle, painter, pasteStyle } from '../editor/style-painter';
import { followLink } from '../editor/bookmarks';
import { detach, goToMaster, insertInstance, makeComponent, resetOverrides, selectedComponent } from '../editor/components';
import ExportDialog from './ExportDialog.vue';
import Icon from './Icon.vue';

const props = defineProps<{ x: number; y: number; target: string | null }>();
const emit = defineEmits<{ close: [] }>();
const app = useApp();
const el = ref<HTMLDivElement | null>(null);
const subEl = ref<HTMLDivElement | null>(null);
const pos = ref({ x: props.x, y: props.y });
const open = ref<'order' | 'export' | null>(null);
const subPos = ref({ left: 0, top: 0 });
const subLeft = ref(false);
const subReady = ref(false);
// the browser sends its own contextmenu event right after a right click ends — on this very backdrop
// when the menu was opened on release (right-button panning): it must not close the menu at once
const openedAt = performance.now();
function onBackdropMenu(): void {
  if (performance.now() - openedAt > 400) emit('close');
}
const raw = ref<string | null>(null);
const exporting = ref<'png' | 'svg' | null>(null);
const has = computed(() => ui.selection.length > 0);
/** API 2.2: commands of modules for this selection. */
const moduleItems = computed(() => (ui.readOnly ? [] : contextMenuFor(selectedObjects.value)));
const ro = computed(() => ui.readOnly);
const one = computed(() => (selectedObjects.value.length === 1 ? selectedObjects.value[0] : null));
const canEditText = computed(() => !!one.value && live.editor?.caps(one.value).editText && !ro.value);

function clampMenu(): void {
  const r = el.value?.getBoundingClientRect();
  if (!r) return;
  const pad = 8;
  pos.value = {
    x: Math.min(Math.max(pad, props.x), Math.max(pad, window.innerWidth - r.width - pad)),
    y: Math.min(Math.max(pad, props.y), Math.max(pad, window.innerHeight - r.height - pad)),
  };
}

onMounted(async () => {
  await nextTick();
  clampMenu();
  (el.value?.querySelector('button:not(:disabled)') as HTMLElement | null)?.focus();
});

/** Screen center of the selection (or the right-clicked object), for which way the submenu opens. */
function objectCenter(): { x: number; y: number } | null {
  const ed = live.editor;
  if (!ed) return null;
  const ids = ui.selection.length ? ui.selection : props.target ? [props.target] : [];
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const id of ids) {
    const b = ed.screenBox(id);
    if (!b) continue;
    x0 = Math.min(x0, b.x);
    y0 = Math.min(y0, b.y);
    x1 = Math.max(x1, b.x + b.w);
    y1 = Math.max(y1, b.y + b.h);
  }
  if (!Number.isFinite(x0)) return null;
  const host = ed.renderer.container.getBoundingClientRect();
  return { x: host.x + (x0 + x1) / 2, y: host.y + (y0 + y1) / 2 };
}

async function showSub(id: 'order' | 'export', trigger?: EventTarget | null): Promise<void> {
  const btn = trigger instanceof HTMLElement ? trigger.closest('button') ?? trigger : el.value?.querySelector<HTMLElement>(`button[data-sub="${id}"]`);
  if (open.value !== id) subReady.value = false;
  open.value = id;
  await nextTick();
  const sub = subEl.value;
  if (!btn || !sub) return;
  const tr = btn.getBoundingClientRect();
  const sr = sub.getBoundingClientRect();
  const menu = el.value?.getBoundingClientRect();
  const obj = objectCenter();
  // open away from the object; if that side does not fit, flip
  let left = obj && menu ? obj.x > menu.left + menu.width / 2 : tr.left + tr.width / 2 > window.innerWidth / 2;
  if (left && tr.left - sr.width < 8) left = false;
  if (!left && tr.right + sr.width > window.innerWidth - 8) left = true;
  subLeft.value = left;
  subPos.value = {
    left: left ? Math.max(8, tr.left - sr.width + 4) : tr.right - 4,
    top: Math.min(Math.max(8, tr.top - 4), window.innerHeight - sr.height - 8),
  };
  subReady.value = true;
}

const ed = () => live.editor;
const openNotes = () => (ui.notesFor = one.value?.id ?? null);
const openLink = () => (ui.linkFor = one.value?.id ?? null);
const follow = () => one.value?.link && followLink(one.value.link);

function run(fn: () => unknown): void {
  emit('close');
  void fn();
}

function onMenuOver(e: PointerEvent): void {
  if (!(e.target instanceof Element) || e.target.closest('[data-sub]')) return;
  open.value = null;
}

function onKey(e: KeyboardEvent): void {
  const root = open.value ? subEl.value : el.value;
  const btn = document.activeElement as HTMLButtonElement | null;
  const items = [...(root?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? [])];
  const i = btn ? items.indexOf(btn) : -1;
  const toward = subLeft.value ? 'ArrowLeft' : 'ArrowRight';
  const back = subLeft.value ? 'ArrowRight' : 'ArrowLeft';
  if (e.key === 'Escape') {
    if (open.value) {
      const parent = el.value?.querySelector<HTMLButtonElement>(`button[data-sub="${open.value}"]`);
      open.value = null;
      parent?.focus();
    } else emit('close');
  } else if (e.key === toward && btn?.dataset['sub']) {
    void showSub(btn.dataset['sub'] as 'order' | 'export', btn);
    void nextTick(() => subEl.value?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus());
  } else if (e.key === back && open.value && subEl.value?.contains(btn)) {
    const parent = el.value?.querySelector<HTMLButtonElement>(`button[data-sub="${open.value}"]`);
    open.value = null;
    parent?.focus();
  } else if (e.key === 'ArrowDown') items[(i + 1) % items.length]?.focus();
  else if (e.key === 'ArrowUp') items[(i - 1 + items.length) % items.length]?.focus();
  else return;
  e.preventDefault();
}
</script>

<template>
  <div class="backdrop" @pointerdown="emit('close')" @contextmenu.prevent="onBackdropMenu"></div>
  <div ref="el" class="menu glass" role="menu" :style="{ left: pos.x + 'px', top: pos.y + 'px' }" @keydown="onKey" @pointerover="onMenuOver">
    <template v-if="has">
      <button role="menuitem" @click="run(() => copyFromMenu(false, app.notify))">{{ t('cmd.copy') }}</button>
      <button role="menuitem" :disabled="ro" @click="run(() => copyFromMenu(true, app.notify))">{{ t('cmd.cut') }}</button>
      <button role="menuitem" @click="run(() => copySelectionAsPng(app.notify))">{{ t('cmd.copyPng') }}</button>
    </template>
    <button role="menuitem" :disabled="ro" @click="run(() => pasteFromMenu(app.notify))">{{ t('cmd.paste') }}</button>
    <template v-if="moduleItems.length">
      <hr />
      <button v-for="c in moduleItems" :key="c.moduleId + c.id" role="menuitem" :data-command="c.moduleId + ':' + c.id" @click="run(() => runCommand(c.moduleId, c.id))">{{ c.label }}</button>
    </template>
    <template v-if="has">
      <button role="menuitem" :disabled="ro" @click="run(() => ed()?.duplicateSelection())">{{ t('cmd.duplicate') }}</button>
      <button role="menuitem" @click="run(() => copyStyle())">{{ t('palette.copyStyle') }}</button>
      <button v-if="painter.style" role="menuitem" :disabled="ro" @click="run(() => pasteStyle())">{{ t('palette.pasteStyle') }}</button>
      <button v-if="one" role="menuitem" data-act="ctx-notes" @click="run(openNotes)">{{ t('notes.open') }}</button>
      <hr v-if="!ro" />
      <button v-if="!ro && !selectedComponent" role="menuitem" data-act="ctx-make-component" @click="run(makeComponent)">{{ t('components.make') }}</button>
      <template v-if="selectedComponent">
        <button v-if="!ro" role="menuitem" data-act="ctx-instance" @click="run(() => insertInstance(selectedComponent!.id))">{{ t('components.insert') }}</button>
        <button v-if="selectedComponent.role === 'instance'" role="menuitem" @click="run(() => goToMaster(selectedComponent!.id))">{{ t('components.goMaster') }}</button>
        <button v-if="!ro && selectedComponent.role === 'instance'" role="menuitem" data-act="ctx-reset-instance" @click="run(() => resetOverrides(selectedComponent!.root.id))">{{ t('components.reset') }}</button>
        <button v-if="!ro && selectedComponent.role === 'instance'" role="menuitem" data-act="ctx-detach" @click="run(() => detach(selectedComponent!.root.id))">{{ t('components.detach') }}</button>
      </template>
      <button v-if="one?.link" role="menuitem" data-act="ctx-follow" @click="run(follow)">{{ t('links.follow') }}</button>
      <button v-if="one && !ro" role="menuitem" data-act="ctx-link" @click="run(openLink)">{{ one.link ? t('links.edit') : t('links.add') }}</button>
      <hr />
      <button v-if="canEditText" role="menuitem" @click="run(() => ed()?.startTextEdit(one!.id))">{{ t('cmd.editText') }}</button>
      <button v-if="!hasGroup" role="menuitem" :disabled="ro || ui.selection.length < 2" @click="run(() => ed()?.groupSelection())">{{ t('cmd.group') }}</button>
      <button v-else role="menuitem" :disabled="ro" @click="run(() => ed()?.ungroupSelection())">{{ t('cmd.ungroup') }}</button>
      <button v-if="one?.type === 'core:frame'" role="menuitem" :disabled="ro" @click="run(() => ed()?.unframeSelection())">{{ t('cmd.unframe') }}</button>
      <button role="menuitem" :disabled="ro" @click="run(() => ed()?.toggleLock())">{{ allLocked ? t('cmd.unlock') : t('cmd.lock') }}</button>
      <hr />
      <button
        role="menuitem"
        data-sub="order"
        :aria-haspopup="true"
        :aria-expanded="open === 'order'"
        :disabled="ro"
        :class="{ open: open === 'order' }"
        @pointerenter="showSub('order', $event.currentTarget)"
        @click="open === 'order' ? (open = null) : showSub('order', $event.currentTarget)"
      >
        {{ t('cmd.order') }}<span :class="{ flip: subLeft && open === 'order' }"><Icon name="chevron" :size="14" /></span>
      </button>
      <button
        role="menuitem"
        data-sub="export"
        :aria-haspopup="true"
        :aria-expanded="open === 'export'"
        :class="{ open: open === 'export' }"
        @pointerenter="showSub('export', $event.currentTarget)"
        @click="open === 'export' ? (open = null) : showSub('export', $event.currentTarget)"
      >
        {{ one?.type === 'core:frame' ? t('cmd.exportFrame') : t('cmd.exportSelection') }}<span :class="{ flip: subLeft && open === 'export' }"><Icon name="chevron" :size="14" /></span>
      </button>
      <button v-if="one && !one.type.startsWith('core:') || one?.type === CoreTypes.group" role="menuitem" @click="raw = JSON.stringify(one, null, 2)">{{ t('cmd.showData') }}</button>
      <button role="menuitem" :disabled="ro" class="danger" @click="run(() => ed()?.deleteSelection())">{{ t('cmd.delete') }}</button>
    </template>
    <template v-else>
      <button role="menuitem" @click="run(() => ed()?.selectAll())">{{ t('cmd.selectAll') }}</button>
      <button role="menuitem" @click="run(() => ed()?.fitAll())">{{ t('cmd.fitAll') }}</button>
    </template>
  </div>
  <div
    v-if="open"
    ref="subEl"
    class="menu glass sub"
    :class="{ ready: subReady }"
    role="menu"
    :style="{ left: subPos.left + 'px', top: subPos.top + 'px' }"
    @keydown="onKey"
    @pointerdown.stop
  >
    <template v-if="open === 'order'">
      <button role="menuitem" :disabled="ro" @click="run(() => ed()?.reorderSelection('front'))">{{ t('cmd.front') }}</button>
      <button role="menuitem" :disabled="ro" @click="run(() => ed()?.reorderSelection('forward'))">{{ t('cmd.forward') }}</button>
      <button role="menuitem" :disabled="ro" @click="run(() => ed()?.reorderSelection('backward'))">{{ t('cmd.backward') }}</button>
      <button role="menuitem" :disabled="ro" @click="run(() => ed()?.reorderSelection('back'))">{{ t('cmd.back') }}</button>
    </template>
    <template v-else>
      <button role="menuitem" @click="exporting = 'png'">PNG</button>
      <button role="menuitem" @click="exporting = 'svg'">SVG</button>
    </template>
  </div>
  <RawDataDialog v-if="raw" :json="raw" @close="(raw = null), emit('close')" />
  <ExportDialog v-if="exporting" :format="exporting" initial-scope="selection" @close="(exporting = null), emit('close')" />
</template>

<style scoped>
.backdrop {
  position: fixed;
  inset: 0;
  z-index: 999;
}
.danger {
  color: var(--danger);
}
.sub {
  z-index: 1001;
  min-width: 180px;
  visibility: hidden;
}
.sub.ready {
  visibility: visible;
}
button.open {
  background: color-mix(in srgb, var(--text) 8%, transparent);
}
.flip {
  display: inline-flex;
  transform: scaleX(-1);
}
</style>
